import { useState } from 'react';
import { ShieldCheck, Plus, Trash2 } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Modal } from '../components/Modal.tsx';
import { useConfirm } from '../components/ConfirmDialog.tsx';
import { useToast } from '../components/Toast.tsx';
import { Reveal } from '../components/motion.tsx';
import './Crud.css';

export function AccessLevels() {
  const levels = trpc.accessLevel.list.useQuery();
  const schedules = trpc.schedule.list.useQuery();
  const doors = trpc.door.list.useQuery();
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const toast = useToast();
  const upsert = trpc.accessLevel.upsert.useMutation({
    onSuccess: () => utils.accessLevel.list.invalidate(),
  });
  const remove = trpc.accessLevel.remove.useMutation({
    onSuccess: () => utils.accessLevel.list.invalidate(),
  });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="stack">
      <Reveal className="crud-head">
        <span className="section-eyebrow">Authorization · Policy</span>
        <h1 className="crud-title">
          <span className="accent">Access levels</span>
        </h1>
        <span className="crud-sub">
          An access level groups (doors × schedule). Cardholders are assigned access levels to gate
          where and when they may enter.
        </span>
      </Reveal>

      <Reveal delay={0.05} className="card tight">
        <div className="row">
          <ShieldCheck size={14} className="muted" />
          <span className="dim">The A22K supports up to 1000 access levels.</span>
          <span className="spacer" />
          <button onClick={() => setShowForm(true)}>
            <Plus size={14} />
            New access level
          </button>
        </div>
      </Reveal>

      <Reveal delay={0.1} className="card">
        {levels.isLoading ? (
          <div className="stack">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skel" style={{ height: 38 }} />
            ))}
          </div>
        ) : !levels.data || levels.data.length === 0 ? (
          <EmptyState
            icon={ShieldCheck}
            title="No access levels"
            body="The A22K supports up to 1000 access levels. Create one to gate doors by schedule."
            action={
              <button onClick={() => setShowForm(true)}>
                <Plus size={14} />
                Create the first one
              </button>
            }
          />
        ) : (
          <table className="table crud-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Schedule</th>
                <th>Doors</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {levels.data.map((l) => (
                <tr key={l.id}>
                  <td style={{ fontWeight: 500 }}>{l.name}</td>
                  <td>
                    {l.scheduleId ? (
                      <Badge tone="info">
                        {schedules.data?.find((s) => s.id === l.scheduleId)?.name ?? '—'}
                      </Badge>
                    ) : (
                      <span className="dim">always</span>
                    )}
                  </td>
                  <td>
                    <Badge>{l.doorIds.length} doors</Badge>
                  </td>
                  <td>
                    <button
                      className="ghost"
                      aria-label={`Delete access level ${l.name}`}
                      title="Delete"
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Delete access level?',
                          body: (
                            <>
                              This will remove <strong>{l.name}</strong> and revoke its grants. This
                              cannot be undone.
                            </>
                          ),
                          confirmLabel: 'Delete',
                          danger: true,
                        });
                        if (!ok) return;
                        await remove.mutateAsync({ id: l.id });
                        toast.success('Access level deleted', l.name);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Reveal>

      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        title="New access level"
        dismissable={!upsert.isPending}
        maxWidth={520}
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const doorIds = (fd.getAll('doorIds') as string[]) ?? [];
            const scheduleId = String(fd.get('scheduleId') ?? '');
            const name = String(fd.get('name'));
            await upsert.mutateAsync({
              name,
              scheduleId: scheduleId || null,
              doorIds,
            });
            toast.success('Access level created', name);
            setShowForm(false);
          }}
          className="stack"
        >
          <input name="name" placeholder="Name (e.g. All Staff)" required />
          <select name="scheduleId" defaultValue="">
            <option value="">Always (no schedule)</option>
            {schedules.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <div className="stack-tight">
            <span className="section-eyebrow">Doors</span>
            <div className="crud-picker">
              {doors.data?.map((d) => (
                <label key={d.id}>
                  <input type="checkbox" name="doorIds" value={d.id} />
                  <span>{d.name}</span>
                </label>
              ))}
              {(!doors.data || doors.data.length === 0) && (
                <span className="dim" style={{ padding: '0.4rem 0.5rem' }}>
                  No doors yet — register a controller first.
                </span>
              )}
            </div>
          </div>
          <div className="dialog-actions">
            <button type="button" className="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </button>
            <button type="submit" disabled={upsert.isPending}>
              {upsert.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
